import Link from 'next/link';
import { styled } from 'styled-components';
import ImageWrapper from '../../../new-components/ImageWrapper';
import { getImagePath } from '../../../helpers/common';
import { useSelector } from 'react-redux';
import CardTags from '../../../new-components/cards/CardTags';
import ImageInfoSection from '../../../new-components/cards/components/ImageInfoSection';
import { EntityTypes } from '../../../new-lib/constants/common';
import CardTitleSection from '../../../new-components/cards/components/CardTitleSection';
import PropertyDetails from '../../../new-components/cards/components/PropertyDetails';
import PriceDetails from '../../../new-components/cards/components/PriceDetails';
import CardCTA from '../../../new-components/cards/components/CardCTA';
import { useTranslation } from 'next-i18next';
import { getNowDate } from '../../../helpers/search-options/common';
import { logEvent } from '../../../new-lib/analytics';

const NawyNowCard = ({ entity }: any) => {
  const isMobile = useSelector((state: any) => state.app.isMobile);
  const { t } = useTranslation('common');

  const handleCardClick = () => {
    logEvent.click(
      'don’t_miss_nawy_now_property_click',
      'nawy_now',
      "don't_miss_properties",
      {
        cmpnd_id: entity.compoundId,
        cmpnd_name: entity.compoundName,
        dev_id: entity.developerId,
        dev_name: entity.developerName,
        prop_type_id: entity.propertyTypeId,
        prop_type_name: entity.propertyTypeName,
        area_id: entity.areaId,
        area_name: entity.areaName,
        payment_plan_id: entity.paymentPlanId,
        price: entity.price
      }
    );
  };

  return (
    <Link
      href={`/compound/${entity.compoundSlug}/property/${entity?.slug}`}
      style={{ width: '100%' }}
      onClick={handleCardClick}
    >
      <Container>
        <UpperSection isNawyNow>
          <ImageWrapper
            src={getImagePath(entity.coverImage)}
            fill
            className="card-image"
            objectFit="cover"
            alt={'nawy-now-property'}
            dataTest="card-image"
            imageSize="high"
            quality={50}
            sizes="70vw"
            withSkeleton
          />

          <CardTags tags={['Nawy Now']} />

          <div className="inner-content">
            <ImageInfoSection
              entityType={EntityTypes.PROPERTY}
              entityData={{
                has_launches: false,
                has_offer: false,
                property_type: { name: 'property' },
                tags: entity?.tags || [],
                logo_path: entity.developerLogoUrl,
                isNawyNow: false,
                min_ready_by: getNowDate()
              }}
            />
          </div>
        </UpperSection>
        <LowerSection isNawyNow={true}>
          <CardTitleSection
            entityData={{
              compoundName: entity?.compoundName,
              propertyType: { name: entity?.propertyType },
              area: `${entity?.areaName}, ${t('egypt')}`,
              logo_path: entity.developerLogoUrl,
              has_offer: false,
              isNawyNow: true
            }}
            entityType={EntityTypes.PROPERTY}
          />

          <PropertyDetails
            entityData={{
              installment_type: entity.installment_type,
              min_ready_by: entity.min_ready_by,
              min_unit_area: entity.unitArea,
              number_of_bathrooms: entity.numberOfBathrooms,
              number_of_bedrooms: entity.numberOfBedrooms,
              isNawyNow: true
            }}
          />

          <div className="price-cta-section">
            <PriceDetails
              entityType={EntityTypes.PROPERTY}
              entityData={{
                max_installment_years: entity.installmentYears,
                max_installment_years_months: undefined,
                min_down_payment: entity.downPaymentValue,
                min_installments: entity.equalInstallmentsValue,
                min_price: entity.price,
                currency: entity.currency,
                // installment_type:
                //   entity?.offers?.[0]?.installment_type?.name ??
                //   entity.installment_type,
                isNawyNow: true
              }}
            />

            {!isMobile && (
              <CardCTA
                clickEventData={{
                  property: entity,
                  eventAction: "don't_miss_section",
                  eventLocation: 'nawy_now',
                  compound: entity.compound,
                  featureName: "don't_miss"
                }}
                entity={entity}
                entityType={EntityTypes.PROPERTY}
              />
            )}
          </div>
        </LowerSection>
      </Container>
    </Link>
  );
};

const Container = styled.div<any>`
  width: 100%;
  height: auto;
  border-radius: 18px;
  overflow: hidden;
  position: relative;
  cursor: pointer;
  margin: 0px 0px 6px 2px;
  .developer-img {
    min-width: 45px;
    max-width: 45px;
    height: 45px;
    background-color: #ffffff;
    border-radius: 50%;
    position: relative;
    overflow: hidden;
  }
  box-shadow: 0px 1px 7px 0px rgba(0, 0, 0, 0.08);
`;

const UpperSection = styled.div<any>`
  position: relative;
  width: 100%;
  height: 173px;
  overflow: hidden;

  > div {
    inset-inline-end: 16px !important;
    inset-inline-start: unset !important;
  }

  .inner-content {
    position: absolute;
    top: 65%;

    width: 100%;
    height: 100%;
    padding: 0px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    background: linear-gradient(360deg, #000000 -25%, rgba(0, 0, 0, 0) 50%);
    .card-action-btns {
      display: flex;
      flex-direction: row;
      gap: 4px;
      justify-content: flex-end;
    }
    .img-lower-section {
      width: 100;
    }
  }

  @media (max-width: ${props => props.theme.breakPoints.mobile.max}) {
    height: ${props => props.isNawyNow && '148px'};
  }
`;

const LowerSection = styled.div<any>`
  /* position: absolute; */
  position: relative;
  height: 159px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(7px);
  margin-top: -10px;
  z-index: 2;
  .price-cta-section {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }

  .installment {
    font-size: 12px;
  }

  .price {
    font-size: 16px;
  }

  .separator {
    display: none;
  }

  .beds-container {
    border-radius: 6px;
    background: rgba(232, 232, 232, 0.6);
    padding: 3px 6px 3px 4px;
  }

  .baths-container {
    border-radius: 6px;
    background: rgba(232, 232, 232, 0.6);
    padding: 3px 6px 3px 4px;
  }

  .area-details {
    border-radius: 6px;
    background: rgba(232, 232, 232, 0.6);
    padding: 3px 6px 3px 4px;
  }

  .property-details {
    margin: 10px 0px;
  }

  @media (max-width: ${props => props.theme.breakPoints.mobile.max}) {
    padding: 12px;
  }
`;

export default NawyNowCard;
